import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Lightbulb, TrendingUp, CircleCheck } from 'lucide-react';
import TrainingWishForm from './TrainingWishForm';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Öffentliches Wunsch-Board für die /kurse Seite:
 * anonymisierte Top-3 Themen-Cluster + eingebettetes Wunsch-Formular.
 */
export const PublicWishBoard = ({ isAuthenticated = false }) => {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/training-wishes/public/top?limit=3`);
        const data = await res.json();
        if (data.success) setClusters(data.clusters || []);
      } catch (e) {
        // silent — Board ist optional
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="py-12 bg-gradient-to-b from-black via-gray-950 to-black" data-testid="public-wish-board">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs uppercase tracking-widest mb-4">
            <TrendingUp className="w-3.5 h-3.5" /> Roadmap
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">Was kommt als Nächstes</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Diese Themen wünschen sich Elektrofachbetriebe in Deutschland gerade am häufigsten. Fehlt Dir was — sag es uns, wir bauen die meistgefragten zuerst.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-5xl mx-auto">
          {loading ? (
            [1,2,3].map((i) => (
              <Card key={i} className="bg-gray-900/60 border-gray-800 animate-pulse">
                <CardContent className="p-5 h-32" />
              </Card>
            ))
          ) : clusters.length > 0 ? (
            clusters.map((c, idx) => (
              <Card key={c.key}
                    className="bg-gradient-to-br from-yellow-500/5 to-transparent border-yellow-500/30 hover:border-yellow-500/60 transition-colors"
                    data-testid={`public-cluster-${c.key}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-3xl font-black text-yellow-400">#{idx + 1}</span>
                    {c.planned > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">
                        <CircleCheck className="w-3 h-3" /> Eingeplant
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{c.label}</h3>
                  <p className="text-sm text-yellow-200/80">
                    {c.requests} {c.requests === 1 ? 'Betrieb' : 'Betriebe'} wünschen dieses Thema
                  </p>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="md:col-span-3 text-center py-8 text-gray-500 text-sm">
              Noch keine öffentlichen Wünsche — sei die erste Stimme.
            </div>
          )}
        </div>

        <div className="max-w-2xl mx-auto">
          <TrainingWishForm isAuthenticated={isAuthenticated} />
          <p className="text-[11px] text-gray-500 text-center mt-3 flex items-center justify-center gap-1">
            <Lightbulb className="w-3 h-3" /> Deine Wünsche zählen 1:1 in die Priorisierung — anonymer Vote möglich.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PublicWishBoard;
